/**
 * Tiles or Rows — how the listing surfaces draw a list.
 *
 * The same shape as floor-state.js, and for the same reason: the toggle lives
 * in the page header, the thing it changes is the grid several components
 * away, and one module has to own the value or they disagree.
 *
 * Unlike the floor, this is not part of the URL and never reaches the server.
 * It changes nothing about *which* listings you see, only their arrangement,
 * so it is a per-device preference in localStorage — putting it in the query
 * string would make every shared link carry the sender's layout, and putting
 * it in a cookie would split the edge cache for a purely visual choice.
 */

export const VIEWS = ['tiles', 'rows'];

export const DEFAULT_VIEW = 'tiles';

export const VIEW_LABELS = { tiles: 'Tiles', rows: 'Rows' };

const STORAGE_KEY = 'uf_view';

const listeners = new Set();
let current = null;

export function isView(value) {
  return VIEWS.includes(value);
}

function read() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    // Private mode, or storage disabled. A preference we cannot remember is
    // not a reason to fail to render.
    return null;
  }
}

function write(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch (error) {
    /* nothing to do: the choice applies to this page and is forgotten */
  }
}

/** The view in force. Resolved once, then cached. */
export function getView() {
  if (current) return current;
  const stored = read();
  current = isView(stored) ? stored : DEFAULT_VIEW;
  return current;
}

/**
 * Change the view. A no-op when it already holds, so a control that re-renders
 * itself on change cannot start a loop.
 *
 * @param {string} view
 * @param {{silent?: boolean}} [options]
 * @returns {string} the view now in force
 */
export function setView(view, options = {}) {
  if (!isView(view)) return getView();
  if (getView() === view) return current;

  current = view;
  write(view);
  if (document.body) document.body.dataset.view = view;

  if (!options.silent) {
    for (const listener of listeners) {
      try {
        listener(view);
      } catch (error) {
        console.error('console-view listener failed', error);
      }
    }
  }
  return current;
}

/**
 * @param {(view: string) => void} callback
 * @returns {() => void} unsubscribe
 */
export function onViewChange(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/** Test seam: forget the resolved value so the next getView() re-reads. */
export function _reset() {
  current = null;
  listeners.clear();
}
